import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { BaseModalComponent } from "@ha/ui/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { catchError, debounceTime, distinctUntilChanged, map, of, switchMap, tap } from "rxjs";

import { FlammableMaterial } from "../../../models/customer-permit-template.model";
import { PermitCreateService } from "../../../services/permit-create.service";

export interface FlammableMaterialSearchModalComponentData {
    subscriptionId: number;
    existingMaterialIds: number[];
    onSelect: (material: FlammableMaterial) => void;
}

@UntilDestroy()
@Component({
    selector: "pmt-flammable-material-search-modal",
    templateUrl: "./flammable-material-search-modal.component.html",
    styleUrls: ["./flammable-material-search-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlammableMaterialSearchModalComponent extends BaseModalComponent implements OnInit {
    public data: FlammableMaterialSearchModalComponentData;

    public searchControl = new FormControl<string>("", { nonNullable: true });
    public searchResults: FlammableMaterial[] = [];
    public isSearching = false;
    public hasSearched = false;

    constructor(private permitCreateService: PermitCreateService, private cdr: ChangeDetectorRef) {
        super();
    }

    public ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                untilDestroyed(this),
                debounceTime(250),
                map((value) => value.trim()),
                distinctUntilChanged(),
                tap((query) => {
                    this.hasSearched = query.length > 0;

                    if (!this.hasSearched) {
                        this.searchResults = [];
                        this.isSearching = false;
                    } else {
                        this.isSearching = true;
                    }

                    this.cdr.markForCheck();
                }),
                switchMap((query) => {
                    if (!query) {
                        return of([] as FlammableMaterial[]);
                    }

                    return this.permitCreateService.searchFlammableMaterials(this.data.subscriptionId, query).pipe(
                        catchError(() => of([] as FlammableMaterial[])),
                    );
                }),
            )
            .subscribe((materials) => {
                const existingMaterialIds = new Set(this.data.existingMaterialIds ?? []);

                this.searchResults = materials
                    .map((material) => this.normalizeMaterial(material))
                    .filter((material) => !existingMaterialIds.has(material.flammableMaterialId));

                this.isSearching = false;
                this.cdr.markForCheck();
            });
    }

    public selectMaterial(material: FlammableMaterial): void {
        if (this.data?.onSelect) {
            this.data.onSelect(material);
        }

        this.closeModal();
    }

    private normalizeMaterial(material: FlammableMaterial): FlammableMaterial {
        const materialId = material.flammableMaterialId ?? material.id;

        return {
            ...material,
            id: material.id ?? materialId,
            flammableMaterialId: materialId,
        };
    }
}
